import React from 'react';

import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import { SearchParamsPetType } from '@rover/constants/src/translations/sitterProfilePage.constants';
import { Box, Flex } from '@rover/kibble/core';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import { useApiBrightHorizonsSitterProfileBannerRetrieve } from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-sitter-profile-banner-retrieve';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import ContactButton from '../../../components/ContactButton';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { BHSitterProfileBanner } from '../BHSitterProfileBanner/BHSitterProfileBanner';

import RevampCancellationPolicy from './RevampCancellationPolicy';
import RevampMobileCtaBarCommon from './RevampMobileCtaCommon';
import RevampMobileCtaPtBannerCommon from './RevampMobileCtaPtBannerCommon';
import { CTAPropsCommon } from './RevampServicesCta';
import { getSitterSlugFromWebUrl } from './utils';

type Props = {
  handleOpenSheet: () => void;
};

const RevampMobileCtaBarOwnerView = ({
  handleOpenSheet,
  priceTransparencyLevel,
  locale,
  petCount,
  petType,
  bookingFeePercentage,
}: Props & CTAPropsCommon): JSX.Element => {
  const { contactUrl } = useRevampProfileContext();
  const {
    sitterData,
    serviceType,
    selectedService,
    cancellationPolicy,
    isBrightHorizonsUser,
    isContactUnavailable,
  } = useRevampCta();
  const currentUser = useCurrentUser();
  const location = useLocationContext();
  const { isVisible: isShortlistVisible, isAdded } = useShortlist();
  // Once the sitter is in the contact list, the footer only offers the contact list actions.
  const isShortlisted = isAdded(sitterData.opk);

  const sitterSlug = getSitterSlugFromWebUrl(sitterData.webUrl);

  const { data: brightHorizonsBanner } = useApiBrightHorizonsSitterProfileBannerRetrieve(
    {
      serviceType: serviceType || 'boarding',
      slug: sitterSlug,
      petType: petType || SearchParamsPetType.DOG_CAT,
      dogCount: petCount.selectedDogCount,
      catCount: petCount.selectedCatCount,
      puppyCount: petCount.selectedPuppyCount,
    },
    {
      query: {
        enabled: isBrightHorizonsUser,
      },
    }
  );

  return (
    <>
      {!isShortlisted && (
        <>
          <Flex justifyContent="space-between" alignItems="center" pb="3x">
            <RevampMobileCtaBarCommon
              handleOpenSheet={handleOpenSheet}
              priceTransparencyLevel={priceTransparencyLevel}
              locale={locale}
              petCount={petCount}
              petType={petType}
            />
          </Flex>

          <RevampMobileCtaPtBannerCommon
            priceTransparencyLevel={priceTransparencyLevel}
            petCount={petCount}
            petType={petType}
            bookingFeePercentage={bookingFeePercentage}
          />

          <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />
        </>
      )}

      <Box pb={isShortlistVisible ? '0x' : '3x'}>
        <ContactButton
          sitterInfo={sitterData}
          sitterSlug={sitterSlug}
          contactUrl={contactUrl}
          isLoggedIn={currentUser.isAuthenticated}
          location={location}
          pagePosition="sticky-footer"
          shouldShowRevampLayout
          disabled={isContactUnavailable}
          webProps={{
            sx: {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
      </Box>

      {!isShortlistVisible && (
        <RevampCancellationPolicy
          cancellationPolicy={cancellationPolicy}
          serviceName={selectedService?.displayName}
          cancellationPolicyDesc={selectedService?.meta?.cancellationPolicyDesc}
          providerId={sitterData.opk}
        />
      )}
    </>
  );
};

export default RevampMobileCtaBarOwnerView;
